import { Curves, Zone } from '../../@types/projects/types';
import {
  LiaoSampleResults,
  LambdaSampleResults
} from '../../@types/samples/types';

export const MIN_CURVE_POINTS = 10;
export const MAX_CURVE_POINTS = 100;
export const DEFAULT_RESULTS_CURVE_POINTS = 50;
export const DEFAULT_SAMPLE_CURVE_POINTS = 50;

type Numeric = number | null | undefined;

export interface ChartPoint {
  x: number;
  y: number;
}

export interface WaterOilRefinementInputs {
  swi: Numeric;
  sor: Numeric;
  no: Numeric;
  nw: Numeric;
  krwEndpoint: Numeric;
  kroEndpoint?: Numeric;
  numberOfPoints: number;
}

export interface GasOilRefinementInputs {
  swi: Numeric;
  sgc: Numeric;
  /**
   * Residual oil that bounds the plotted Sg range (curve end at
   * Sg = 1 - swi - sor, the material-balance Sgmax).
   */
  sor: Numeric;
  no: Numeric;
  ng: Numeric;
  krgEndpoint: Numeric;
  kroEndpoint?: Numeric;
  numberOfPoints: number;
  /**
   * Residual oil used for the Corey saturation normalization
   * (Son/Sgn denominator). Gas-oil refinement fits No/Ng/Krg' against
   * the critical residual Sorc (the gas `sor` field, ~0.05), so the
   * plotted curve must reuse Sorc to reproduce those parameters —
   * normalizing with the material-balance Sorg instead lands Krg' at
   * the wrong saturation and the curve overshoots the lab data.
   * Defaults to `sor` (unchanged behaviour) when omitted.
   */
  normSor?: Numeric;
}

const isFiniteNumber = (value: Numeric): value is number =>
  typeof value === 'number' && Number.isFinite(value);

export const round = (value: number, decimals: number): number => {
  if (!Number.isFinite(value)) return value;
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
};

export const clampCurvePointCount = (value: Numeric, fallback = DEFAULT_RESULTS_CURVE_POINTS): number => {
  const raw = isFiniteNumber(value) ? Math.trunc(value) : fallback;
  return Math.min(MAX_CURVE_POINTS, Math.max(MIN_CURVE_POINTS, raw));
};

const sanitizeValues = (arr: number[]): number[] => arr.map(v => (Number.isFinite(v) ? v : 0));

// Noise floor of the zone-value pipeline (4-decimal point rounding,
// 4-decimal derived lambda, fixed-precision equation coefficients): tiny
// negative Pc tails near the free-water level are numerical artifacts, not
// physics. Snap them to 0 so tables and validators don't flag noise.
// Mirrored in backend use_cases/auto_correlation/curve_validation.py.
const PC_NOISE_TOLERANCE = 2e-3;

const clampNoise = (value: number, tolerance: number): number =>
  Number.isFinite(value) && value < 0 && value > -tolerance ? 0 : value;

const linspace = (start: number, end: number, count: number, decimals = 4): number[] => {
  // Callers clamp the total curve size. Do not clamp this internal segment:
  // Water curves reserve one final endpoint, so clamping `n - 1` back to the
  // public minimum produced one extra point at the minimum setting.
  const n = Math.max(1, Math.trunc(count));
  if (n === 1) return [round(start, decimals)];
  const denominator = n - 1;
  return Array.from({ length: n }, (_, index) => {
    const value = start + ((end - start) * (index / denominator));
    return round(value, decimals);
  });
};

const buildWaterSaturationCurve = (swir: number, sorW: number, count: number): number[] => {
  const n = clampCurvePointCount(count);
  if (n === 1) return [round(swir, 4)];
  const mobileEnd = round(1 - sorW, 4);
  const mobilePoints = linspace(swir, mobileEnd, n - 1);
  return [...mobilePoints, 1.0];
};

const buildGasSaturationCurve = (swir: number, sorG: number, sgc: number, count: number): number[] => {
  const n = clampCurvePointCount(count);
  const last = round(1 - swir - sorG, 4);
  if (n === 1) return [0];
  if (n === 2) return [0, last];
  const tail = Array.from({ length: n - 2 }, (_, index) => {
    const ratio = (index + 1) / (n - 2);
    return round(sgc + ((last - sgc) * ratio), 4);
  });
  return [0, round(sgc, 4), ...tail].slice(0, n);
};

export const buildLambdaSampleCurve = (
  values: Pick<LambdaSampleResults, 'pe' | 'lambda_value'> & { pc_max?: Numeric },
  swir: Numeric,
  numberOfPoints: number
): ChartPoint[] => {
  if (
    !isFiniteNumber(values.pe) ||
    !isFiniteNumber(values.lambda_value) ||
    !isFiniteNumber(values.pc_max) ||
    !isFiniteNumber(swir) ||
    1 - swir <= 0
  ) {
    return [];
  }

  const pe = values.pe;
  const lambdaValue = values.lambda_value;
  const pcMax = values.pc_max;

  const n = clampCurvePointCount(numberOfPoints, DEFAULT_SAMPLE_CURVE_POINTS);
  const swValues = linspace(swir, 1, n);
  return swValues
    .map((sw, index): ChartPoint => {
      if (index === 0) return { x: sw, y: pcMax };
      const swd = (sw - swir) / (1 - swir);
      const pc = pe * Math.pow(swd, lambdaValue);
      return { x: sw, y: pc };
    })
    .filter(point => Number.isFinite(point.x) && Number.isFinite(point.y) && point.y > 0);
};

export const buildLiaoSampleCurve = (
  values: Pick<
    LiaoSampleResults,
    'best_a' | 'best_b' | 'lambda_variable' | 'swir' | 'true_swir' | 'true_pc_max' | 'pc_max' | 'pce'
  >,
  numberOfPoints: number
): ChartPoint[] => {
  const {
    best_a,
    best_b,
    lambda_variable,
    swir,
    true_swir,
    true_pc_max,
    pc_max,
    pce
  } = values;

  if (
    !isFiniteNumber(best_a) ||
    !isFiniteNumber(best_b) ||
    !isFiniteNumber(lambda_variable) ||
    !isFiniteNumber(swir) ||
    !isFiniteNumber(true_swir) ||
    !isFiniteNumber(true_pc_max) ||
    !isFiniteNumber(pc_max) ||
    !isFiniteNumber(pce) ||
    1 - swir <= 0
  ) {
    return [];
  }

  const n = clampCurvePointCount(numberOfPoints, DEFAULT_SAMPLE_CURVE_POINTS);
  const remaining = Math.max(0, n - 1);
  const swValues = remaining > 0 ? linspace(swir, 1, remaining) : [];
  const pcDValues = swValues.map(sw => {
    const swd = (sw - swir) / (1 - swir);
    return ((1 + best_a) / Math.pow(1 + best_b * swd, 1 / lambda_variable)) - best_a;
  });
  const finitePcDValues = pcDValues.filter(Number.isFinite);
  const minPcD = finitePcDValues.length ? Math.min(...finitePcDValues) : pce;
  const modelPoints = swValues.map((sw, index) => {
    const pcD = pcDValues[index];
    const pc = (pcD * pc_max) + ((1 - pcD) * minPcD);
    return { x: sw, y: pc };
  });

  return [{ x: true_swir, y: true_pc_max }, ...modelPoints]
    .slice(0, n)
    .filter(point => Number.isFinite(point.x) && Number.isFinite(point.y));
};

const calculateHeight = (pcow: number[], gradDiff: number): number[] =>
  gradDiff === 0 ? new Array(pcow.length).fill(0) : pcow.map(value => round(value / gradDiff, 2));

const calculateScaled = (endpoint: number, curve: number[]): number[] =>
  endpoint === 0 ? new Array(curve.length).fill(0) : curve.map(value => round(value / endpoint, 4));

export const buildWaterOilRefinementCurves = ({
  swi,
  sor,
  no,
  nw,
  krwEndpoint,
  kroEndpoint = 1,
  numberOfPoints
}: WaterOilRefinementInputs): { sw: number[]; kro: number[]; krw: number[] } => {
  if (
    !isFiniteNumber(swi) ||
    !isFiniteNumber(sor) ||
    !isFiniteNumber(no) ||
    !isFiniteNumber(nw) ||
    !isFiniteNumber(krwEndpoint) ||
    !isFiniteNumber(kroEndpoint) ||
    1 - swi - sor <= 0
  ) {
    return { sw: [], kro: [], krw: [] };
  }

  const sw = linspace(swi, 1 - sor, numberOfPoints);
  const denom = 1 - swi - sor;
  const kro = sw.map(value => round(kroEndpoint * Math.pow(Math.max((1 - value - sor) / denom, 0), no), 4));
  const krw = sw.map(value => round(krwEndpoint * Math.pow(Math.max((value - swi) / denom, 0), nw), 4));
  return { sw, kro, krw };
};

export const buildGasOilRefinementCurves = ({
  swi,
  sgc,
  sor,
  no,
  ng,
  krgEndpoint,
  kroEndpoint = 1,
  numberOfPoints,
  normSor
}: GasOilRefinementInputs): { sg: number[]; kro: number[]; krg: number[] } => {
  // Normalization residual defaults to `sor`; callers pass Sorc when
  // the exponents were fit on the critical-residual axis.
  const effectiveNormSor = isFiniteNumber(normSor) ? normSor : sor;
  if (
    !isFiniteNumber(swi) ||
    !isFiniteNumber(sgc) ||
    !isFiniteNumber(sor) ||
    !isFiniteNumber(no) ||
    !isFiniteNumber(ng) ||
    !isFiniteNumber(krgEndpoint) ||
    !isFiniteNumber(kroEndpoint) ||
    !isFiniteNumber(effectiveNormSor) ||
    1 - swi - sor <= 0 ||
    1 - swi - sgc - effectiveNormSor <= 0
  ) {
    return { sg: [], kro: [], krg: [] };
  }

  const sg = linspace(sgc, 1 - swi - sor, numberOfPoints);
  const denom = 1 - swi - sgc - effectiveNormSor;
  const kro = sg.map(value => round(kroEndpoint * Math.pow(Math.max((1 - swi - value - effectiveNormSor) / denom, 0), no), 4));
  const krg = sg.map(value => round(krgEndpoint * Math.pow(Math.max((value - sgc) / denom, 0), ng), 4));
  return { sg, kro, krg };
};

export const buildLiaoZoneCurves = (zone: Zone, gradDiff: number, numberOfPoints: number): Curves | null => {
  const values = zone.zone_values?.liao_values;
  const kroAbsoluteG = isFiniteNumber(values?.kro_absolute_g)
    ? values?.kro_absolute_g
    : values?.kro_absolute;
  if (
    !values ||
    !isFiniteNumber(gradDiff) ||
    !isFiniteNumber(values.swir) ||
    !isFiniteNumber(values.best_a) ||
    !isFiniteNumber(values.best_b) ||
    !isFiniteNumber(values.pce) ||
    !isFiniteNumber(values.lambda_variable) ||
    !isFiniteNumber(values.true_pc_max) ||
    !isFiniteNumber(values.nw) ||
    !isFiniteNumber(values.no_w) ||
    !isFiniteNumber(values.ng) ||
    !isFiniteNumber(values.krw_absolute) ||
    !isFiniteNumber(values.kro_absolute) ||
    !isFiniteNumber(kroAbsoluteG) ||
    !isFiniteNumber(values.krg_absolute) ||
    !isFiniteNumber(values.sor_w) ||
    !isFiniteNumber(values.sor_g) ||
    !isFiniteNumber(values.sgc)
  ) {
    return null;
  }

  const count = clampCurvePointCount(numberOfPoints);
  const sw = buildWaterSaturationCurve(values.swir, values.sor_w, count);
  const pcow = sw.map(value => {
    const term1 = (1 + values.best_a!) * (values.true_pc_max! - values.pce!);
    const swNorm = (value - values.swir!) / (1 - values.swir!);
    const denom = Math.pow((values.best_b! * swNorm) + 1, 1 / values.lambda_variable!);
    const term2 = values.pce! - values.best_a! * (values.true_pc_max! - values.pce!);
    return clampNoise(round((term1 / denom) + term2, 4), PC_NOISE_TOLERANCE);
  });
  const krw = sw.map(value => round(values.krw_absolute! * Math.pow(Math.max((value - values.swir!) / (1 - values.swir! - values.sor_w!), 0), values.nw!), 4));
  const krow = sw.map((value, index) => (
    index === sw.length - 1
      ? 0
      : round(values.kro_absolute! * Math.pow(Math.max((1 - value - values.sor_w!) / (1 - values.swir! - values.sor_w!), 0), values.no_w!), 4)
  ));
  const sg = buildGasSaturationCurve(values.swir, values.sor_g, values.sgc, count);
  const krg = sg.map(value => round(values.krg_absolute! * Math.pow(Math.max((value - values.sgc!) / (1 - values.sgc! - values.swir! - values.sor_g!), 0), values.ng!), 4));
  const krog = sg.map((value, index) => (
    index === sg.length - 1
      ? 0
      : round(kroAbsoluteG! * Math.pow(Math.max(((1 - value - values.swir!) - values.sor_w!) / (1 - values.swir! - values.sor_w!), 0), values.no_w!), 4)
  ));

  return {
    sw: sanitizeValues(sw),
    pcow: sanitizeValues(pcow),
    krw: sanitizeValues(krw),
    krow: sanitizeValues(krow),
    height: sanitizeValues(calculateHeight(pcow, gradDiff)),
    sg: sanitizeValues(sg),
    krg: sanitizeValues(krg),
    krog: sanitizeValues(krog),
    krw_scaled: sanitizeValues(calculateScaled(values.kro_absolute, krw)),
    krow_scaled: sanitizeValues(calculateScaled(values.kro_absolute, krow)),
    krg_scaled: sanitizeValues(calculateScaled(kroAbsoluteG, krg)),
    krog_scaled: sanitizeValues(calculateScaled(kroAbsoluteG, krog))
  };
};

export const buildLambdaZoneCurves = (zone: Zone, gradDiff: number, numberOfPoints: number): Curves | null => {
  const values = zone.zone_values?.lambda_values;
  const kroAbsoluteG = isFiniteNumber(values?.kro_absolute_g)
    ? values?.kro_absolute_g
    : values?.kro_absolute;
  if (
    !values ||
    !isFiniteNumber(gradDiff) ||
    !isFiniteNumber(values.swir) ||
    !isFiniteNumber(values.pe) ||
    !isFiniteNumber(values.lambda_value) ||
    !isFiniteNumber(values.true_pc_max) ||
    !isFiniteNumber(values.nw) ||
    !isFiniteNumber(values.no_w) ||
    !isFiniteNumber(values.ng) ||
    !isFiniteNumber(values.krw_absolute) ||
    !isFiniteNumber(values.kro_absolute) ||
    !isFiniteNumber(kroAbsoluteG) ||
    !isFiniteNumber(values.krg_absolute) ||
    !isFiniteNumber(values.sor_w) ||
    !isFiniteNumber(values.sor_g) ||
    !isFiniteNumber(values.sgc)
  ) {
    return null;
  }

  const count = clampCurvePointCount(numberOfPoints);
  const sw = buildWaterSaturationCurve(values.swir, values.sor_w, count);
  const pcow = sw.map(value => {
    const base = ((value - values.swir!) / (1 - values.swir!)) + Math.pow(values.true_pc_max! / values.pe!, 1 / (-values.lambda_value!));
    return clampNoise(round(values.pe! * Math.pow(base, -values.lambda_value!), 4), PC_NOISE_TOLERANCE);
  });
  const krw = sw.map(value => round(values.krw_absolute! * Math.pow(Math.max((value - values.swir!) / (1 - values.swir! - values.sor_w!), 0), values.nw!), 4));
  const krow = sw.map((value, index) => (
    index === sw.length - 1
      ? 0
      : round(values.kro_absolute! * Math.pow(Math.max((1 - value - values.sor_w!) / (1 - values.swir! - values.sor_w!), 0), values.no_w!), 4)
  ));
  const sg = buildGasSaturationCurve(values.swir, values.sor_g, values.sgc, count);
  const krg = sg.map(value => round(values.krg_absolute! * Math.pow(Math.max((value - values.sgc!) / (1 - values.sgc! - values.swir! - values.sor_g!), 0), values.ng!), 4));
  const krog = sg.map((value, index) => (
    index === sg.length - 1
      ? 0
      : round(kroAbsoluteG! * Math.pow(Math.max(((1 - value - values.swir!) - values.sor_w!) / (1 - values.swir! - values.sor_w!), 0), values.no_w!), 4)
  ));

  return {
    sw: sanitizeValues(sw),
    pcow: sanitizeValues(pcow),
    krw: sanitizeValues(krw),
    krow: sanitizeValues(krow),
    height: sanitizeValues(calculateHeight(pcow, gradDiff)),
    sg: sanitizeValues(sg),
    krg: sanitizeValues(krg),
    krog: sanitizeValues(krog),
    krw_scaled: sanitizeValues(calculateScaled(values.kro_absolute, krw)),
    krow_scaled: sanitizeValues(calculateScaled(values.kro_absolute, krow)),
    krg_scaled: sanitizeValues(calculateScaled(kroAbsoluteG, krg)),
    krog_scaled: sanitizeValues(calculateScaled(kroAbsoluteG, krog))
  };
};

export const buildProjectCurves = (
  project: { correlation?: { zones?: Zone[]; grad_diff?: number } | null },
  numberOfPoints: number
) => {
  const zones = project.correlation?.zones ?? [];
  const gradDiff = project.correlation?.grad_diff;
  if (!isFiniteNumber(gradDiff)) return project;

  zones.forEach(zone => {
    const liao = buildLiaoZoneCurves(zone, gradDiff, numberOfPoints);
    const lambda = buildLambdaZoneCurves(zone, gradDiff, numberOfPoints);
    zone.zone_curves = {
      liao_curves: liao,
      lambda_curves: lambda
    };
  });

  return project;
};
